/**
 * Строка фильтра темы над сводкой вкладки (#2229, спека §2.5, §3): «Все блоки
 * вкладки — по разговорам с темой «…»: K из N разговоров периода» и «Снять».
 * Оба числа — из эха `themeFilter` одного ответа вкладки; каждый блок
 * по-прежнему пишет свой «K из N». Прошлое окно темы несравнимо — строка
 * называет причину, почему на вкладке нет сдвигов. Эха нет — «фильтр темы
 * недоступен»: числа вкладки не показываются вовсе. «Снять» снимает тему тем
 * же патчем, что выбор «Все темы» (`themeFilterPatch`).
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { VoiceThemeFilterTab } from './useVoiceThemeFilter';
import { themeFilterPatch, type VoiceSearch } from './voice-search';
import { daysSince } from './voice-theme-filter-view-model';

interface VoiceThemeFilterStripProps {
  /** The tab's filter (`useVoiceThemeFilterTab`); no theme selected — no strip. */
  filter: VoiceThemeFilterTab;
  /** The answer whose echo gives K hit the cap: «at least K», as in the blocks. */
  truncated: boolean;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
}

export function VoiceThemeFilterStrip({
  filter,
  truncated,
  onSearchChange,
}: VoiceThemeFilterStripProps) {
  const { t } = useTranslation('dashboard');
  // A theme being lifted after a refusal: the section header says why.
  if (filter.strip === null || filter.refused) return null;
  const { verdict, name, since } = filter.strip;
  if (verdict.kind === 'none') return null;
  const lines =
    verdict.kind === 'applied'
      ? [
          t(
            truncated
              ? 'voicePage.themeFilter.scopeAtLeast'
              : 'voicePage.themeFilter.scope',
            { name, calls: verdict.echo.calls, count: verdict.echo.windowCalls }
          ),
          ...(verdict.echo.prevComparable
            ? []
            : [
                verdict.echo.prevIncomparable
                  ? t('voicePage.themeFilter.noComparisonRevision')
                  : since === undefined
                    ? t('voicePage.themeFilter.noComparison')
                    : t('voicePage.themeFilter.noComparisonSince', {
                        count: daysSince(since, new Date()),
                      }),
              ]),
        ]
      : verdict.kind === 'pending'
        ? [t('voicePage.themeFilter.scopeLoading', { name })]
        : [t('voicePage.themeFilter.unavailable', { name })];
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border bg-bg-surface px-4 py-3"
      data-testid="voice-theme-filter-strip"
      role="status"
    >
      <div className="space-y-1">
        {lines.map((line) => (
          <p key={line} className="text-sm text-fg">
            {line}
          </p>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onSearchChange(themeFilterPatch(undefined))}
      >
        {t('voicePage.themeFilter.clear')}
      </Button>
    </div>
  );
}
